import type { IShopifyPageAssignedResponse } from '~/modules/shopify/types';
import type { UseQueryOptions } from '@tanstack/react-query';
import type { TemplateType } from '~/types/template';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchFromAPI } from '~/api/utils';

const queryKey = 'shopifyAssigned';
export type ShopifyTemplateAssignedQueryVariables = {
  type: TemplateType;
  clearCache?: boolean;
};

export const useQueryShopifyTemplateAssigned = <
  TData = IShopifyPageAssignedResponse,
  TError = unknown,
>(
  variables?: ShopifyTemplateAssignedQueryVariables,
  options?: UseQueryOptions<IShopifyPageAssignedResponse, TError, TData>,
) => {
  const queryClient = useQueryClient();

  const api = async (variables?: ShopifyTemplateAssignedQueryVariables) => {
    const pageType = variables?.type;
    const endpoint = `/api/shopify/assigned?type=${pageType} `;
    return fetchFromAPI(endpoint, {
      method: 'POST',
      body: JSON.stringify(variables),
    });
  };

  const fetchManual = async (
    variables?: ShopifyTemplateAssignedQueryVariables,
    isClearCache?: boolean,
  ): Promise<{ data: IShopifyPageAssignedResponse }> => {
    const cacheKey = useQueryShopifyTemplateAssigned.getKey(variables);
    const cachedData = queryClient.getQueryData<IShopifyPageAssignedResponse>(cacheKey);

    if (cachedData && !isClearCache) {
      return { data: cachedData };
    }

    const response = (await api(variables)) as IShopifyPageAssignedResponse;
    queryClient.setQueryData(cacheKey, response);
    return { data: response };
  };

  return {
    ...useQuery<IShopifyPageAssignedResponse, TError, TData>(
      useQueryShopifyTemplateAssigned.getKey(variables),
      () => api(variables),
      {
        enabled: variables !== undefined,
        ...options,
      },
    ),
    fetchManual,
  };
};

useQueryShopifyTemplateAssigned.getKey = (variables?: ShopifyTemplateAssignedQueryVariables) =>
  variables === undefined ? [queryKey] : [queryKey, variables];
